export type ExtractedInvoiceClient = {
  name?: string;
  email?: string;
  phone?: string;
};

export type ExtractedInvoiceProject = {
  name?: string;
  eventType?: string;
  eventDate?: string;
  location?: string;
};

const PHONE_LIKE = /^[\d\s\-+().]{7,}$/;

const MONTH_PREFIX: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

function monthIndex(token: string): number | null {
  const key = token.trim().toLowerCase().slice(0, 3);
  return key in MONTH_PREFIX ? MONTH_PREFIX[key]! : null;
}

export function parseInvoiceHeaderDate(left: string): string | null {
  const trimmed = left.trim();
  if (!trimmed) return null;
  const withoutDow = trimmed.replace(/^[A-Za-z]{3,9},\s*/i, "").trim();
  const match = /^([A-Za-z]+)\s+(\d{1,2}),?\s*(\d{4})$/.exec(withoutDow);
  if (!match) return null;
  const month = monthIndex(match[1]!);
  if (month == null) return null;
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (!Number.isFinite(day) || day < 1 || day > 31 || !Number.isFinite(year)) return null;
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function splitLines(text: string): string[] {
  return text.split(/\r?\n/).map((line) => line.trim());
}

function isMajorSectionHeader(low: string): boolean {
  return low.includes("project details") || low.includes("service info");
}

function isBillToAddressBlockEnd(low: string): boolean {
  return isMajorSectionHeader(low) || low.includes("date issued");
}

function isLikelyInvoiceMetaNameLine(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  const low = trimmed.toLowerCase();
  if (/^(?:\d+-)+\d+$/i.test(trimmed)) return true;
  if (/^invoice\s*#/.test(low)) return true;
  if (/^invoice\s+no\.?\s*[:#]?\s*\w+/i.test(trimmed)) return true;
  return false;
}

export function sanitizeExtractedClientNameLine(raw: string): string {
  let value = raw.trim();
  if (!value) return value;
  let previous = "";
  while (previous !== value) {
    previous = value;
    value = value
      .replace(/\s+\d{2,}(?:-\d+)+$/i, "")
      .replace(/\s+#[\d\-]{4,}$/i, "")
      .replace(/\s+\d{9,}$/, "")
      .trim();
  }
  return value;
}

export function extractInvoiceHeaderContext(text: string): {
  client: ExtractedInvoiceClient;
  project: ExtractedInvoiceProject;
} {
  const client: ExtractedInvoiceClient = {};
  const project: ExtractedInvoiceProject = {};
  if (!text) return { client, project };

  const lines = splitLines(text);
  const billIdx = lines.findIndex((line) => line.toLowerCase().includes("bill to"));
  if (billIdx >= 0) {
    let index = billIdx + 1;
    while (index < lines.length && lines[index] === "") index += 1;
    const limit = Math.min(lines.length, billIdx + 14);
    for (; index < limit; index += 1) {
      const line = lines[index];
      if (!line) continue;
      const low = line.toLowerCase();
      if (isMajorSectionHeader(low)) break;
      if (isLikelyInvoiceMetaNameLine(line)) continue;
      const cleaned = sanitizeExtractedClientNameLine(line);
      if (!cleaned) continue;
      client.name = cleaned;
      index += 1;
      break;
    }

    let filledEmail = false;
    let filledPhone = false;
    for (; index < limit; index += 1) {
      const line = lines[index];
      if (!line) continue;
      const low = line.toLowerCase();
      if (isBillToAddressBlockEnd(low)) break;
      if (isLikelyInvoiceMetaNameLine(line)) continue;
      if (!filledEmail && line.includes("@")) {
        client.email = line;
        filledEmail = true;
        continue;
      }
      if (!filledPhone && PHONE_LIKE.test(line) && /\d{3,}/.test(line.replace(/\D/g, ""))) {
        client.phone = line;
        filledPhone = true;
      }
    }
  }

  const projectIdx = lines.findIndex((line) => line.toLowerCase().includes("project details"));
  if (projectIdx >= 0) {
    let index = projectIdx + 1;
    while (index < lines.length && lines[index] === "") index += 1;
    if (index < lines.length && lines[index]) {
      const row = lines[index]!;
      const segments = row.split("|").map((part) => part.trim()).filter(Boolean);
      if (segments.length >= 2) {
        project.name = segments[0];
        project.eventType = segments[segments.length - 1];
      } else {
        project.name = row;
      }
      index += 1;
    }
    while (index < lines.length && lines[index] === "") index += 1;
    if (index < lines.length && lines[index]) {
      const row = lines[index]!;
      const segments = row.split("|").map((part) => part.trim());
      const left = (segments[0] ?? "").trim();
      const right = segments.length > 1 ? segments.slice(1).join(" | ").trim() : "";
      const iso = parseInvoiceHeaderDate(left);
      if (iso) project.eventDate = iso;
      if (right) project.location = (right.split(",")[0]?.trim() || right).slice(0, 500);
    }
  }

  return { client, project };
}
